/**
 * Browser-local Automerge storage, as two scopes of one IndexedDB database.
 * Tenant docs (files, pinned, checkpoints) belong to the workspace and stay.
 * User docs (chats, later per-user config) are keyed by member id, and every
 * page load collects the subtrees that aren't the signed-in member's — so
 * they outlive their session only until the next load.
 */
import { IndexedDBStorageAdapter } from "@automerge/react";
import type {
  Chunk,
  StorageAdapterInterface,
  StorageKey,
} from "@automerge/react";

import { fetchSession, SESSION_QUERY_KEY } from "./auth-client";
import { queryClient } from "./query-client";

const DATABASE = "automerge";
const OBJECT_STORE = "documents";
const USER_SEGMENT = "user";

export type StoreScope = "tenant" | "user";

/** `"unknown"` is a failed session lookup. It must never read as a sign-out. */
export type StoreOwner = { id: string } | "nobody" | "unknown";

/** The app-debug embed surface is throwaway and persists nothing. */
export const isEmbedSurface =
  typeof window !== "undefined" &&
  (window as { __HEARTH_SURFACE__?: string }).__HEARTH_SURFACE__ === "embed";

const persists = typeof window !== "undefined" && !isEmbedSurface;

/** Tenant keys sit at the root — the layout already on every device. */
export function scopePrefix(
  scope: StoreScope,
  owner: StoreOwner,
): StorageKey | null {
  if (scope === "tenant") return [];
  return typeof owner === "string" ? null : [USER_SEGMENT, owner.id];
}

/**
 * One scope's view of the shared database. Keys are prefixed going in and
 * stripped coming out: compaction hands `loadRange`'s keys back to `remove`
 * and reads them positionally. No `close` — the connection is shared, and
 * `Repo.shutdown()` would take the workspace's down with a chat unmount.
 */
class ScopedStore implements StorageAdapterInterface {
  readonly #inner: StorageAdapterInterface;
  readonly #prefix: StorageKey;

  constructor(inner: StorageAdapterInterface, prefix: StorageKey) {
    this.#inner = inner;
    this.#prefix = prefix;
  }

  #scoped(key: StorageKey): StorageKey {
    return [...this.#prefix, ...key];
  }

  load(key: StorageKey): Promise<Uint8Array | undefined> {
    return this.#inner.load(this.#scoped(key));
  }

  save(key: StorageKey, data: Uint8Array): Promise<void> {
    return this.#inner.save(this.#scoped(key), data);
  }

  saveBatch(entries: Array<[StorageKey, Uint8Array]>): Promise<void> {
    return this.#inner.saveBatch(
      entries.map(([key, data]) => [this.#scoped(key), data]),
    );
  }

  remove(key: StorageKey): Promise<void> {
    return this.#inner.remove(this.#scoped(key));
  }

  async loadRange(keyPrefix: StorageKey): Promise<Chunk[]> {
    const chunks = await this.#inner.loadRange(this.#scoped(keyPrefix));
    return chunks.map((chunk) => ({
      ...chunk,
      key: chunk.key.slice(this.#prefix.length),
    }));
  }

  removeRange(keyPrefix: StorageKey): Promise<void> {
    return this.#inner.removeRange(this.#scoped(keyPrefix));
  }
}

export function scopedStore(
  inner: StorageAdapterInterface,
  prefix: StorageKey,
): StorageAdapterInterface {
  return new ScopedStore(inner, prefix);
}

/** Member ids the user scope holds data for, read off the keys themselves. */
export function ownersInKeys(keys: readonly StorageKey[]): string[] {
  const owners = new Set<string>();
  for (const key of keys) {
    const owner = key[0] === USER_SEGMENT ? key[1] : undefined;
    if (typeof owner === "string") owners.add(owner);
  }
  return [...owners];
}

export interface PruneEnv {
  store: StorageAdapterInterface;
  keys: () => Promise<StorageKey[]>;
}

/** Keep this member's subtree, collect the rest. Returns what it collected. */
export async function pruneUserScope(
  env: PruneEnv,
  owner: StoreOwner,
): Promise<string[]> {
  if (owner === "unknown") return [];
  const keep = owner === "nobody" ? null : owner.id;
  const foreign = ownersInKeys(await env.keys()).filter((id) => id !== keep);
  for (const id of foreign) await env.store.removeRange([USER_SEGMENT, id]);
  return foreign;
}

let connection: IndexedDBStorageAdapter | undefined;

function sharedConnection(): IndexedDBStorageAdapter {
  connection ??= new IndexedDBStorageAdapter(DATABASE);
  return connection;
}

/** Keys only — `loadRange` would pull every document's bytes to answer this. */
function allKeys(): Promise<StorageKey[]> {
  return new Promise((resolve, reject) => {
    const open = indexedDB.open(DATABASE, 1);
    open.onupgradeneeded = () => {
      if (!open.result.objectStoreNames.contains(OBJECT_STORE))
        open.result.createObjectStore(OBJECT_STORE);
    };
    open.onerror = () => reject(open.error);
    open.onsuccess = () => {
      const db = open.result;
      const request = db
        .transaction(OBJECT_STORE)
        .objectStore(OBJECT_STORE)
        .getAllKeys();
      request.onerror = () => {
        db.close();
        reject(request.error);
      };
      request.onsuccess = () => {
        db.close();
        resolve(request.result.filter(Array.isArray) as StorageKey[]);
      };
    };
  });
}

/** Rides `useSession`'s cached request. A throw is "unknown", not "nobody". */
const ownerReady: Promise<StoreOwner> = !persists
  ? Promise.resolve("unknown")
  : queryClient
      .ensureQueryData({ queryKey: SESSION_QUERY_KEY, queryFn: fetchSession })
      .then((session): StoreOwner =>
        session ? { id: session.user.id } : "nobody",
      )
      .catch((err: unknown): StoreOwner => {
        console.warn("session lookup failed; chats run storage-less", err);
        return "unknown";
      });

/** Fire-and-forget: prefixes already isolate, so this only collects. */
export const storeReady: Promise<void> = !persists
  ? Promise.resolve()
  : ownerReady
      .then((owner) =>
        pruneUserScope({ store: sharedConnection(), keys: allKeys }, owner),
      )
      .then((collected) => {
        if (collected.length > 0)
          console.info(`local store: collected ${collected.length} member(s)`);
      })
      .catch((err: unknown) => {
        console.warn("local store prune failed", err);
      });

/** The one door. `undefined` means run storage-less. */
export async function openStore(
  scope: StoreScope,
): Promise<StorageAdapterInterface | undefined> {
  if (!persists) return undefined;
  const prefix = scopePrefix(
    scope,
    scope === "user" ? await ownerReady : "unknown",
  );
  return prefix === null ? undefined : scopedStore(sharedConnection(), prefix);
}
